import { FilterContentType, TableFilter } from "../../../types/tableFilter";
import { useListFilterValues, useTimeConverter } from "../../../hooks/hooks";
import { useTranslation } from "react-i18next";
import { Gender } from "../../../types/common";
import { DataSearchTestType } from "../../../types/dataSearch";
import { DataSearchFilterValues } from "./useDataSearchFilterValues";

interface Args {
  search: () => void;
}

export function useDataSearchFilters({ search }: Args) {
  const { filterValues } = useListFilterValues();
  const dataSearchFilterValues = filterValues as DataSearchFilterValues;
  const { t } = useTranslation("dataSearch");
  const { timeConverter } = useTimeConverter();

  const {
    qFilterValue: qFilter,
    ageYearFilterValue: ageYearFilter,
    genderFilterValue: genderFilter,
    stateFilterValue: stateFilter,
    cityFilterValue: cityFilter,
    endedAtFromFilterValue: endedAtFromFilter,
    endedAtToFilterValue: endedAtToFilter,
    testTypeFilterValue: testTypeFilter,
    onlyCompletedFilterValue: onlyCompletedFilter,
    excludeTestAccountFilterValue: excludeTestAccountFilter,
  } = dataSearchFilterValues;

  const filters: TableFilter[][] = [
    [
      {
        labelTitle: t("filter.searchWord.label"),
        content: {
          type: FilterContentType.TEXT,
          value: qFilter?.value,
          maxLength: 100,
          placeholderVal: t("filter.searchWord.placeholder"),
          onChangeFunc: (v) => qFilter?.setter(v),
          onEnterKeyUpFunc: search,
        },
      },
    ],
    [
      {
        labelTitle: t("filter.endedAt.label"),
        content: {
          type: FilterContentType.CALENDAR_RANGE,
          from: {
            type: FilterContentType.CALENDAR,
            value: timeConverter.convertToFilterDateString(endedAtFromFilter?.value),
            onChangeFunc: (v) =>
              endedAtFromFilter?.setter(v ? timeConverter.convertToFilterStartOfDay(v) : undefined),
            placeholderVal: t("filter.endedAt.placeholder"),
          },
          to: {
            type: FilterContentType.CALENDAR,
            value: timeConverter.convertToFilterDateString(endedAtToFilter?.value),
            onChangeFunc: (v) => endedAtToFilter?.setter(v ? timeConverter.convertToFilterEndOfDay(v) : undefined),
            placeholderVal: t("filter.endedAt.placeholder"),
          },
        },
      },
      {
        labelTitle: t("filter.testType.label"),
        content: {
          type: FilterContentType.SELECT,
          value: testTypeFilter?.value ?? "",
          options: [
            { value: "", label: t("filter.all") },
            { value: DataSearchTestType.KPASS, label: t("filter.testType.kpass") },
            { value: DataSearchTestType.DCAS, label: t("filter.testType.dcas") },
          ],
          onChangeFunc: (v) => testTypeFilter?.setter(v as DataSearchTestType | ""),
        },
      },
      {
        labelTitle: t("filter.gender.label"),
        content: {
          type: FilterContentType.SELECT,
          value: genderFilter?.value ?? "",
          options: [
            { value: "", label: t("filter.all") },
            { value: Gender.MALE, label: t("filter.gender.male") },
            { value: Gender.FEMALE, label: t("filter.gender.female") },
          ],
          onChangeFunc: (v) => genderFilter?.setter(v as Gender | ""),
        },
      },
      {
        labelTitle: t("filter.ageYear.label"),
        content: {
          type: FilterContentType.NUMBER,
          value: ageYearFilter?.value,
          min: 0,
          max: 100,
          onChangeFunc: (v) => ageYearFilter?.setter(v),
          placeholderVal: t("filter.ageYear.placeholder"),
        },
      },
    ],
    [
      {
        labelTitle: t("filter.state.label"),
        content: {
          type: FilterContentType.TEXT,
          value: stateFilter?.value,
          maxLength: 50,
          placeholderVal: t("filter.state.placeholder"),
          onChangeFunc: (v) => stateFilter?.setter(v),
          onEnterKeyUpFunc: search,
        },
      },
      {
        labelTitle: t("filter.city.label"),
        content: {
          type: FilterContentType.TEXT,
          value: cityFilter?.value,
          maxLength: 50,
          placeholderVal: t("filter.city.placeholder"),
          onChangeFunc: (v) => cityFilter?.setter(v),
          onEnterKeyUpFunc: search,
        },
      },
    ],
    [
      {
        labelTitle: t("filter.onlyCompleted.label"),
        content: {
          type: FilterContentType.CHECK_BOX,
          isChecked: onlyCompletedFilter?.value ?? false,
          onChangeFunc: (v: boolean) => onlyCompletedFilter?.setter(v),
        } as any,
      },
      {
        labelTitle: t("filter.excludeTestAccount.label"),
        content: {
          type: FilterContentType.CHECK_BOX,
          isChecked: excludeTestAccountFilter?.value ?? false,
          onChangeFunc: (v: boolean) => excludeTestAccountFilter?.setter(v),
        } as any,
      },
    ],
  ];

  return {
    filters,
  };
}
